import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { ArrowLeft, CheckCircle2 } from 'lucide-react';
import { api } from '@/lib/api';
import type { GeneralRequestForm, Allergen, VenueArea } from '@/types';

export default function FormPreview() {
  const { venueId, formId } = useParams();
  const navigate = useNavigate();
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: form, isLoading } = useQuery<GeneralRequestForm>({
    queryKey: ['form', formId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/forms/${formId}`);
      return response.data;
    },
  });

  const { data: allergens } = useQuery<Allergen[]>({
    queryKey: ['allergens'],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/forms/allergens`);
      return response.data;
    },
  });

  const { data: areas } = useQuery<VenueArea[]>({
    queryKey: ['venue-areas', venueId],
    queryFn: async () => {
      const response = await api.get(`/admin/venues/${venueId}/areas`);
      return response.data;
    },
  });

  const { register, handleSubmit, watch, setValue } = useForm();

  const validateField = (fieldType: string, value: string): string | null => {
    if (!value) return null;

    switch (fieldType) {
      case 'email':
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          return 'Please enter a valid email address';
        }
        break;
      case 'mobile':
        if (!/^(?:(?:\+44)|(?:0))7\d{9}$/.test(value.replace(/\s/g, ''))) {
          return 'Please enter a valid UK mobile number';
        }
        break;
      case 'phone':
        if (!/^(?:(?:\+44)|(?:0))(?:1\d{8,9}|2\d{9}|3\d{9}|7\d{9}|8\d{9})$/.test(value.replace(/\s/g, ''))) {
          return 'Please enter a valid UK phone number';
        }
        break;
      case 'postcode':
        if (!/^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(value)) {
          return 'Please enter a valid UK postcode';
        }
        break;
    }
    return null;
  };

  const onSubmit = (data: Record<string, unknown>) => {
    const newErrors: Record<string, string> = {};

    form?.fields?.forEach((field) => {
      const value = data[field.id];

      // Check required
      if (field.is_required && !value) {
        newErrors[field.id] = 'This field is required';
        return;
      }

      // Validate field type
      if (value && typeof value === 'string') {
        const error = validateField(field.field_type, value);
        if (error) {
          newErrors[field.id] = error;
        }
      }

      // Check min/max for numbers
      if (field.field_type === 'number' || field.field_type === 'guest_count') {
        const numValue = Number(value);
        if (field.config?.min !== undefined && numValue < field.config.min) {
          newErrors[field.id] = `Minimum value is ${field.config.min}`;
        }
        if (field.config?.max !== undefined && numValue > field.config.max) {
          newErrors[field.id] = `Maximum value is ${field.config.max}`;
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    setSubmitted(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center py-12">
          <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Submission Successful</h2>
          <p className="text-gray-600 mb-6">
            {form?.success_message || 'Thank you for your submission.'}
          </p>
          <div className="flex items-center justify-center gap-4">
            <button
              onClick={() => setSubmitted(false)}
              className="btn btn-secondary"
            >
              Submit Another
            </button>
            <button
              onClick={() => navigate(`/venues/${venueId}/forms/${formId}`)}
              className="btn btn-primary"
            >
              Back to Editor
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex items-center gap-4 mb-6">
        <button
          onClick={() => navigate(`/venues/${venueId}/forms/${formId}`)}
          className="p-2 hover:bg-gray-100 rounded-md"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">Preview: {form?.name}</h1>
          <p className="text-sm text-gray-500">
            This is how the form will appear to users
          </p>
        </div>
      </div>

      <div className="card">
        {form?.intro_text && (
          <p className="text-gray-600 mb-6">{form.intro_text}</p>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {form?.fields
            ?.filter((f) => f.is_active)
            .sort((a, b) => a.sort_order - b.sort_order)
            .map((field) => (
              <div key={field.id}>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {field.label}
                  {field.is_required && <span className="text-red-500 ml-1">*</span>}
                </label>

                {/* Text */}
                {field.field_type === 'text' && (
                  <input
                    type="text"
                    {...register(field.id)}
                    placeholder={field.placeholder}
                    className={`input ${errors[field.id] ? 'border-red-500' : ''}`}
                  />
                )}

                {/* Textarea */}
                {field.field_type === 'textarea' && (
                  <textarea
                    {...register(field.id)}
                    placeholder={field.placeholder}
                    rows={field.config?.rows || 4}
                    className={`input ${errors[field.id] ? 'border-red-500' : ''}`}
                  />
                )}

                {/* Number / Guest Count */}
                {(field.field_type === 'number' || field.field_type === 'guest_count') && (
                  <input
                    type="number"
                    {...register(field.id)}
                    placeholder={field.placeholder}
                    min={field.config?.min}
                    max={field.config?.max}
                    className={`input ${errors[field.id] ? 'border-red-500' : ''}`}
                  />
                )}

                {/* Email */}
                {field.field_type === 'email' && (
                  <input
                    type="email"
                    {...register(field.id)}
                    placeholder={field.placeholder || 'email@example.com'}
                    className={`input ${errors[field.id] ? 'border-red-500' : ''}`}
                  />
                )}

                {/* Phone / Mobile */}
                {(field.field_type === 'phone' || field.field_type === 'mobile') && (
                  <input
                    type="tel"
                    {...register(field.id)}
                    placeholder={
                      field.placeholder ||
                      (field.field_type === 'mobile' ? '07xxx xxxxxx' : '01onal xxxxxx')
                    }
                    className={`input ${errors[field.id] ? 'border-red-500' : ''}`}
                  />
                )}

                {/* Postcode */}
                {field.field_type === 'postcode' && (
                  <input
                    type="text"
                    {...register(field.id)}
                    placeholder={field.placeholder || 'SW1A 1AA'}
                    className={`input ${errors[field.id] ? 'border-red-500' : ''}`}
                  />
                )}

                {/* Date / Venue Date */}
                {(field.field_type === 'date' || field.field_type === 'venue_date') && (
                  <input
                    type="date"
                    {...register(field.id)}
                    className={`input ${errors[field.id] ? 'border-red-500' : ''}`}
                  />
                )}

                {/* Time / Venue Time */}
                {(field.field_type === 'time' || field.field_type === 'venue_time') && (
                  <input
                    type="time"
                    {...register(field.id)}
                    className={`input ${errors[field.id] ? 'border-red-500' : ''}`}
                  />
                )}

                {/* DateTime */}
                {field.field_type === 'datetime' && (
                  <input
                    type="datetime-local"
                    {...register(field.id)}
                    className={`input ${errors[field.id] ? 'border-red-500' : ''}`}
                  />
                )}

                {/* Dropdown / Venue Area */}
                {(field.field_type === 'dropdown' || field.field_type === 'venue_area') && (
                  <select
                    {...register(field.id)}
                    className={`input ${errors[field.id] ? 'border-red-500' : ''}`}
                  >
                    <option value="">Select an option...</option>
                    {field.field_type === 'venue_area'
                      ? areas?.map((area) => (
                          <option key={area.id} value={area.id}>
                            {area.name}
                          </option>
                        ))
                      : field.options?.map((opt) => (
                          <option key={opt.id} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                  </select>
                )}

                {/* Radio */}
                {field.field_type === 'radio' && (
                  <div className="space-y-2">
                    {field.options?.map((opt) => (
                      <label key={opt.id} className="flex items-center gap-2">
                        <input
                          type="radio"
                          {...register(field.id)}
                          value={opt.value}
                        />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Checkbox */}
                {field.field_type === 'checkbox' && (
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      {...register(field.id)}
                      className="rounded border-gray-300 text-indigo-600"
                    />
                    <span className="text-sm">{field.placeholder || 'Yes'}</span>
                  </label>
                )}

                {/* Checkbox Group */}
                {field.field_type === 'checkbox_group' && (
                  <div className="space-y-2">
                    {field.options?.map((opt) => (
                      <label key={opt.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          value={opt.value}
                          onChange={(e) => {
                            const current = (watch(field.id) as string[]) || [];
                            if (e.target.checked) {
                              setValue(field.id, [...current, opt.value]);
                            } else {
                              setValue(
                                field.id,
                                current.filter((v) => v !== opt.value)
                              );
                            }
                          }}
                          className="rounded border-gray-300 text-indigo-600"
                        />
                        <span className="text-sm">{opt.label}</span>
                      </label>
                    ))}
                  </div>
                )}

                {/* Allergens */}
                {field.field_type === 'allergens' && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {allergens?.map((allergen) => (
                      <label key={allergen.id} className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          value={allergen.id}
                          onChange={(e) => {
                            const current = (watch(field.id) as string[]) || [];
                            if (e.target.checked) {
                              setValue(field.id, [...current, allergen.id]);
                            } else {
                              setValue(
                                field.id,
                                current.filter((v) => v !== allergen.id)
                              );
                            }
                          }}
                          className="rounded border-gray-300 text-indigo-600"
                        />
                        <span className="text-sm">{allergen.name}</span>
                      </label>
                    ))}
                  </div>
                )}

                {errors[field.id] && (
                  <p className="text-sm text-red-600 mt-1">{errors[field.id]}</p>
                )}

                {field.help_text && !errors[field.id] && (
                  <p className="text-xs text-gray-500 mt-1">{field.help_text}</p>
                )}
              </div>
            ))}

          <button type="submit" className="w-full btn btn-primary">
            Submit Request
          </button>
        </form>
      </div>
    </div>
  );
}
